import { Card, Table, Button, Tag, Spin } from 'antd';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getVirtualAccountBySafeHavenId,
	clearVirtualAccountResult,
} from '../../methods/redux/reducers/payment-gateway';

const StyledCard = styled(Card)`
	.ant-card-body {
		padding: 24px;
	}
`;

const StatusTag = styled(Tag)`
	font-weight: 500;
	padding: 4px 12px;
	border-radius: 4px;
`;

const StyledTable = styled(Table)`
	.table-row-light {
		background-color: #ffffff;
	}
	.table-row-dark {
		background-color: #fafafa;
	}
	.ant-table-tbody > tr:hover > td {
		background-color: #e6f7ff !important;
	}
`;

const SafeHavenVirtualAccountDetails = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { virtualAccountResult } = useAppSelector(
		(store) => store.paymentGateway
	) as { virtualAccountResult: any };

	useEffect(() => {
		if (id) {
			dispatch(getVirtualAccountBySafeHavenId(id));
		}
		return () => {
			dispatch(clearVirtualAccountResult());
		};
	}, [id, dispatch]);

	const handleBack = () => {
		navigate('/core-modules/virtual-fiat-accounts');
	};

	if (!virtualAccountResult) {
		return (
			<StyledCard>
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
					<p style={{ marginTop: '16px' }}>
						Loading virtual account details...
					</p>
				</div>
			</StyledCard>
		);
	}

	const accountData = (virtualAccountResult as any)?.data?.data;

	// Prepare table data
	const tableData = [
		{
			key: 'status',
			label: 'Status',
			value: accountData?.status || 'N/A',
			type: 'status',
		},
		{
			key: 'id',
			label: 'ID',
			value: accountData?._id || 'N/A',
			type: 'text',
		},
		{
			key: 'accountNumber',
			label: 'Account Number',
			value: accountData?.accountNumber || 'N/A',
			type: 'text',
		},
		{
			key: 'settlementAccountNumber',
			label: 'Settlement Account Number',
			value: accountData?.settlementAccount?.accountNumber || 'N/A',
			type: 'text',
		},
		{
			key: 'settlementAccountBankCode',
			label: 'Settlement Account Bank Code',
			value: accountData?.settlementAccount?.bankCode || 'N/A',
			type: 'text',
		},
		{
			key: 'bankCode',
			label: 'Bank Code',
			value: accountData?.bankCode || 'N/A',
			type: 'text',
		},
		{
			key: 'bvn',
			label: 'BVN',
			value: accountData?.bvn || 'N/A',
			type: 'text',
		},
		{
			key: 'externalReference',
			label: 'External Reference',
			value: accountData?.externalReference || 'N/A',
			type: 'text',
		},
		{
			key: 'isDeleted',
			label: 'Is Deleted',
			value: accountData?.isDeleted ? 'True' : 'False',
			type: 'boolean',
		},
		{
			key: 'callbackUrl',
			label: 'Callback URL',
			value: accountData?.callbackUrl || 'N/A',
			type: 'text',
		},
		{
			key: 'validFor',
			label: 'Valid For',
			value: accountData?.validFor || 'N/A',
			type: 'text',
		},
		{
			key: 'amount',
			label: 'Amount',
			value: accountData?.amount
				? new Intl.NumberFormat('en-NG', {
						style: 'currency',
						currency: 'NGN',
				  }).format(accountData.amount)
				: 'N/A',
			type: 'currency',
		},
		{
			key: 'amountControl',
			label: 'Amount Control',
			value: accountData?.amountControl || 'N/A',
			type: 'text',
		},
		{
			key: 'expiryDate',
			label: 'Expires',
			value: accountData?.expiryDate
				? new Date(accountData.expiryDate).toLocaleString()
				: 'N/A',
			type: 'date',
		},
		{
			key: 'createdAt',
			label: 'Created At',
			value: accountData?.createdAt
				? new Date(accountData.createdAt).toLocaleString()
				: 'N/A',
			type: 'date',
		},
	];

	const columns = [
		{
			title: 'Field',
			dataIndex: 'label',
			key: 'label',
			width: '30%',
			render: (text: string) => (
				<strong style={{ color: '#1890ff' }}>{text}</strong>
			),
		},
		{
			title: 'Value',
			dataIndex: 'value',
			key: 'value',
			width: '70%',
			render: (text: string, record: any) => {
				if (record.type === 'status') {
					const statusColors: any = {
						active: 'green',
						pending: 'orange',
						deleted: 'red',
						closed: 'default',
					};
					const color = statusColors[text?.toLowerCase()] || 'default';
					return (
						<StatusTag color={color}>{text?.toUpperCase() || 'N/A'}</StatusTag>
					);
				}
				if (record.type === 'boolean') {
					return <Tag color={text === 'True' ? 'red' : 'green'}>{text}</Tag>;
				}
				return <span style={{ wordBreak: 'break-word' }}>{text}</span>;
			},
		},
	];

	return (
		<StyledCard>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div>
					<h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
						Safe Haven Virtual Account Details
					</h1>
					<p style={{ margin: '8px 0 0 0', color: '#666' }}>
						Virtual Account ID: {id}
					</p>
				</div>
				<Button onClick={handleBack}>Back to List</Button>
			</div>

			{(virtualAccountResult as any)?.status && (
				<div
					style={{
						marginBottom: '24px',
						padding: '12px',
						background: '#f0f8ff',
						borderRadius: '4px',
					}}
				>
					<p style={{ margin: '4px 0' }}>
						<strong>Response Status:</strong>{' '}
						<Tag
							color={
								(virtualAccountResult as any).status === 'success'
									? 'green'
									: 'orange'
							}
						>
							{(virtualAccountResult as any).status}
						</Tag>
					</p>
					{(virtualAccountResult as any)?.status_code && (
						<p style={{ margin: '4px 0' }}>
							<strong>Status Code:</strong>{' '}
							{(virtualAccountResult as any).status_code}
						</p>
					)}
					{(virtualAccountResult as any)?.data?.message && (
						<p style={{ margin: '4px 0' }}>
							<strong>Message:</strong>{' '}
							{(virtualAccountResult as any).data.message}
						</p>
					)}
				</div>
			)}

			<StyledTable
				columns={columns}
				dataSource={tableData}
				pagination={false}
				bordered
				style={{
					backgroundColor: '#fff',
					borderRadius: '8px',
					overflow: 'hidden',
				}}
				rowClassName={(record, index) =>
					index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
				}
			/>
		</StyledCard>
	);
};

export default SafeHavenVirtualAccountDetails;
